import React, { useMemo, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import resolveImageUrl from "../../utils/imageUtils";
import { motion, AnimatePresence } from "framer-motion";

// ─── field aliases (same as admin bulk upload) ───────────────────────────────
const FIELD_ALIASES = {
  occupationstatus: ["occupationstatus", "occupation_status"],
  location: ["location", "address"],
  exp_garv: ["exp_garv", "expected_garv", "expgarv"],
  admin_garv: ["admin_garv", "admingarv"],
  pin: ["pin", "pin_id", "property_pin", "property_id", "id", "pin_number", "pin_no", "pinno"],
  circle: ["circle"],
  floor_admin: ["floor_admin", "floor", "floors", "no_of_floors", "no_of_floor", "total_floors", "stories", "storeys"],
  landuse_admin: ["landuse_admin", "land_use_admin", "admin_land_use", "admin_landuse", "landuse", "land_use", "landuse_type", "land_use_type", "land_type", "usage"],
  coveredarea_admin: ["coveredarea_admin", "covered_area_admin", "coverederarea_admin", "covereder_area_admin", "coveredarea", "covered_area", "coveredarea_sq_ft", "covered_area_sq_ft", "coveredarea_sqft", "covered_area_sqft"],
  landarea_admin: ["landarea_admin", "land_area_admin", "admin_landarea", "admin_land_area", "landarea", "land_area", "landarea_sq_yds", "land_area_sq_yds", "landarea_sqyds", "land_area_sqyds"],
  floors_pred: ["floors_pred", "floors_ml"],
  land_use_pred: ["land_use_pred", "landuse_ml"],
  coveredarea_combined_pred: ["coveredarea_combined_pred", "coveredarea_ml"],
  landarea_combined_pred: ["landarea_combined_pred", "landarea_ml"],
  floors_ml: ["floors_ml", "floors_pred"],
  landuse_ml: ["landuse_ml", "land_use_pred"],
  coveredarea_ml: ["coveredarea_ml", "coverederarea_ml", "covereder_area_ml", "coveredarea_combined_pred"],
  landarea_ml: ["landarea_ml", "landarea_combined_pred"],
  refno: ["refno", "reference_no", "reference_number", "pt5_reference", "pt_5_reference"],
  reassess: ["reassess"],
  group: ["group", "groupes"],
  p_name: ["p_name", "inspector_name"],
};

const normalizeKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const getValueFromRow = (row, aliases) => {
  const normalized = Object.entries(row).reduce((acc, [k, v]) => {
    acc[normalizeKey(k)] = v;
    return acc;
  }, Object.create(null));
  const match = aliases.find(
    (alias) => normalized[alias] !== undefined && normalized[alias] !== null
  );
  return match ? normalized[match] : "";
};

const getPinFromFileName = (name) =>
  String(name || "")
    .replace(/\.[^/.]+$/, "")
    .trim();

// NOTE: Frontend rotateToPortrait has been DISABLED (commented out).
// Rationale: Browser EXIF handling is inconsistent across vendors — some browsers return
// raw pixel dimensions for img.width/height while drawing EXIF-corrected pixels via
// ctx.drawImage(). This mismatch caused double-rotation of mobile photos taken in
// portrait orientation, resulting in wrongly oriented saved images. The backend Sharp
// pipeline (sharp().rotate() -> panorama check -> force-portrait) handles EXIF reliably
// across all clients; re-enable this helper only if you need client-side pre-processing.
//
// const PANORAMA_ASPECT_RATIO_THRESHOLD = 2.5;
//
// const rotateToPortrait = (file) =>
//   new Promise((resolve, reject) => {
//     const img = new Image();
//     const url = URL.createObjectURL(file);
//     img.onload = () => {
//       try {
//         const { width, height } = img;
//
//         if (height >= width) {
//           URL.revokeObjectURL(url);
//           resolve(file);
//           return;
//         }
//
//         const aspectRatio = width / height;
//         if (aspectRatio > PANORAMA_ASPECT_RATIO_THRESHOLD) {
//           URL.revokeObjectURL(url);
//           resolve(file);
//           return;
//         }
//
//         const canvas = document.createElement("canvas");
//         canvas.width = height;
//         canvas.height = width;
//         const ctx = canvas.getContext("2d");
//         if (!ctx) { URL.revokeObjectURL(url); resolve(file); return; }
//         ctx.translate(canvas.width / 2, canvas.height / 2);
//         ctx.rotate(Math.PI / 2);
//         ctx.drawImage(img, -width / 2, -height / 2);
//         canvas.toBlob(
//           (blob) => {
//             URL.revokeObjectURL(url);
//             if (!blob) { resolve(file); return; }
//             resolve(new File([blob], file.name, { type: "image/jpeg" }));
//           },
//           "image/jpeg",
//           0.92
//         );
//       } catch (error) {
//         URL.revokeObjectURL(url);
//         reject(error);
//       }
//     };
//     img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Cannot load ${file.name}`)); };
//     img.src = url;
//   });

// ─── Main Component ───────────────────────────────────────────────────────────
const SupervisorBulkUpload = () => {
  const user = JSON.parse(localStorage.getItem("user")) || { id: "", name: "Unknown" };

  const [excelData, setExcelData] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // For viewing uploaded properties
  const [myUploads, setMyUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [activeTab, setActiveTab] = useState("upload"); // "upload" | "view"

  // Property detail modal
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState("");
  const [rotation, setRotation] = useState(0);

  // ── derived data ──
  const excelPins = useMemo(
    () => new Set(excelData.map((row) => String(row.pin || "").trim()).filter(Boolean)),
    [excelData]
  );

  const matchedImages = useMemo(() => {
    const matches = {};
    selectedImages.forEach((file) => {
      const pin = getPinFromFileName(file.name);
      if (excelPins.has(pin)) matches[pin] = file;
    });
    return matches;
  }, [selectedImages, excelPins]);

  const unmatchedImages = useMemo(
    () => selectedImages.filter((file) => !excelPins.has(getPinFromFileName(file.name))),
    [selectedImages, excelPins]
  );

  const excelRowByPin = useMemo(() => {
    const map = {};
    excelData.forEach((row) => { if (row.pin) map[row.pin] = row; });
    return map;
  }, [excelData]);

  const readSheetFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = (e) => resolve(e.target?.result);
      const name = String(file?.name || "").toLowerCase();
      if (name.endsWith(".csv")) reader.readAsText(file);
      else reader.readAsArrayBuffer(file);
    });

  const parseRowsFromFile = (file, fileContent) => {
    const name = String(file?.name || "").toLowerCase();
    if (name.endsWith(".csv")) {
      const parsed = Papa.parse(String(fileContent || ""), {
        header: true,
        skipEmptyLines: true,
      });
      if (parsed.errors?.length) {
        throw new Error(parsed.errors[0]?.message || "CSV parse error");
      }
      return Array.isArray(parsed.data) ? parsed.data : [];
    }

    const buffer = new Uint8Array(fileContent);
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames?.[0];
    if (!sheetName) return [];
    const worksheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
  };

  // ── Excel handler ──
  const handleExcelUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    readSheetFile(file)
      .then((fileContent) => {
        const rows = parseRowsFromFile(file, fileContent);
        const formatted = rows
          .map((row) => ({
            occupationstatus: getValueFromRow(row, FIELD_ALIASES.occupationstatus),
            location: getValueFromRow(row, FIELD_ALIASES.location),
            exp_garv: getValueFromRow(row, FIELD_ALIASES.exp_garv),
            admin_garv: getValueFromRow(row, FIELD_ALIASES.admin_garv),
            pin: String(getValueFromRow(row, FIELD_ALIASES.pin) || "").trim(),
            circle: getValueFromRow(row, FIELD_ALIASES.circle),
            floor_admin: getValueFromRow(row, FIELD_ALIASES.floor_admin),
            landuse_admin: getValueFromRow(row, FIELD_ALIASES.landuse_admin),
            coveredarea_admin: getValueFromRow(row, FIELD_ALIASES.coveredarea_admin),
            landarea_admin: getValueFromRow(row, FIELD_ALIASES.landarea_admin),
            floors_pred: getValueFromRow(row, FIELD_ALIASES.floors_pred),
            land_use_pred: getValueFromRow(row, FIELD_ALIASES.land_use_pred),
            coveredarea_combined_pred: getValueFromRow(row, FIELD_ALIASES.coveredarea_combined_pred),
            landarea_combined_pred: getValueFromRow(row, FIELD_ALIASES.landarea_combined_pred),
            floors_ml: getValueFromRow(row, FIELD_ALIASES.floors_ml),
            landuse_ml: getValueFromRow(row, FIELD_ALIASES.landuse_ml),
            coveredarea_ml: getValueFromRow(row, FIELD_ALIASES.coveredarea_ml),
            landarea_ml: getValueFromRow(row, FIELD_ALIASES.landarea_ml),
            refno: getValueFromRow(row, FIELD_ALIASES.refno),
            reassess: getValueFromRow(row, FIELD_ALIASES.reassess),
            group: getValueFromRow(row, FIELD_ALIASES.group),
            p_name: getValueFromRow(row, FIELD_ALIASES.p_name),
            uploaded_by: user.id,
          }))
          .filter((row) => row.pin);

        // deduplicate by pin
        const uniqueByPin = [];
        const seenPins = new Set();
        formatted.forEach((row) => {
          if (!seenPins.has(row.pin)) {
            seenPins.add(row.pin);
            uniqueByPin.push(row);
          }
        });

        setExcelData(uniqueByPin);
        toast.success(`${uniqueByPin.length} unique rows loaded`);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Invalid file format");
        setExcelData([]);
      });
  };

  // ── Images handler ──
  const handleImagesUpload = (event) => {
    const files = Array.from(event.target.files || []);
    const jpgFiles = files.filter((file) =>
      /image\/jpeg|image\/jpg|image\/png/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name)
    );
    const uniqueImagesByPin = [];
    const seenImagePins = new Set();
    jpgFiles.forEach((file) => {
      const pin = getPinFromFileName(file.name);
      if (!seenImagePins.has(pin)) {
        seenImagePins.add(pin);
        uniqueImagesByPin.push(file);
      }
    });
    setSelectedImages(uniqueImagesByPin);
    toast.success(`${uniqueImagesByPin.length} unique JPG/PNG images selected`);
  };

  // ── Upload handler ──
  const uploadAll = async () => {
    if (!excelData.length) {
      toast.error("Please upload Excel data first");
      return;
    }
    setSubmitting(true);
    try {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const postWithRetry = async (url, body, config) => {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            return await axios.post(url, body, config);
          } catch (error) {
            const status = error?.response?.status;
            const retryable = status === 429 || (status >= 500 && status <= 599) || !status;
            if (!retryable || attempt === maxAttempts) throw error;
            await sleep(500 * attempt);
          }
        }
      };

      // Step 1: bulk insert properties (supervisor-only table)
      const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
        return result;
      };

      const batches = chunkArray(excelData, 50);
      for (const batch of batches) {
        await postWithRetry(
          "/supervisor-properties/bulk",
          { properties: batch, userId: user.id },
          { timeout: 180000 }
        );
      }

      // Step 2: attach images one by one
      let imageSuccess = 0;
      let imageFailed = 0;

      for (const pin of excelData.map((r) => r.pin)) {
        const imageFile = matchedImages[pin];
        if (!imageFile) continue;
        try {
          const formData = new FormData();
          formData.append("image", imageFile, `${pin}.jpg`);
          formData.append("userId", user.id);

          const row = excelRowByPin[pin] || {};
          Object.keys(row).forEach((k) => {
            if (k !== "uploaded_by") formData.append(k, row[k] || "");
          });

          await axios.put(
            `/supervisor-properties/image/${encodeURIComponent(pin)}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }
          );
          imageSuccess++;
        } catch (err) {
          console.error(`Image upload failed for PIN ${pin}`, err);
          imageFailed++;
        }
      }

      toast.success(
        `Uploaded ${excelData.length} rows. Images attached: ${imageSuccess}. Failed: ${imageFailed}.`
      );
      setExcelData([]);
      setSelectedImages([]);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Bulk upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Fetch my uploads ──
  const fetchMyUploads = async () => {
    setLoadingUploads(true);
    try {
      const res = await axios.get(`/supervisor-properties/my-uploads/${user.id}`);
      setMyUploads(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load your uploads");
    } finally {
      setLoadingUploads(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "view") fetchMyUploads();
  };

  // ── Modal helpers ──
  const openProperty = (prop) => {
    setSelectedProperty(prop);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedProperty(null);
  };

  const handleImageClick = (e, url) => {
    e.stopPropagation();
    setFullscreenImageUrl(url);
    setShowFullscreenImage(true);
    setRotation(0);
  };

  const closeFullscreen = () => {
    setShowFullscreenImage(false);
    setFullscreenImageUrl("");
    setRotation(0);
  };

  // ── Delete a single uploaded property ──
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this property from your uploads?")) return;
    try {
      await axios.delete(`/supervisor-properties/${id}`);
      toast.success("Property deleted");
      setMyUploads((prev) => prev.filter((p) => p.id !== id));
      if (selectedProperty?.id === id) closePopup();
    } catch (err) {
      toast.error("Failed to delete property");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-100 to-teal-50">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-teal-800 tracking-wide">
              📋 My Property Uploads
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              Upload properties visible only to you. These cannot be assigned to inspectors.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => handleTabChange("upload")}
              className={`px-5 py-2 text-sm font-semibold rounded-t-lg transition-all ${activeTab === "upload"
                  ? "bg-teal-600 text-white border-b-2 border-teal-600"
                  : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              Upload New
            </button>
            <button
              onClick={() => handleTabChange("view")}
              className={`px-5 py-2 text-sm font-semibold rounded-t-lg transition-all ${activeTab === "view"
                  ? "bg-teal-600 text-white border-b-2 border-teal-600"
                  : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              View My Uploads
            </button>
          </div>

          {/* ── UPLOAD TAB ── */}
          {activeTab === "upload" && (
            <div className="space-y-6">
              {/* Step cards */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Excel */}
                <section className="bg-white rounded-2xl shadow-md p-6 border border-teal-100">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-7 h-7 rounded-full bg-teal-600 text-white text-sm font-bold flex items-center justify-center">1</span>
                    <h2 className="text-lg font-semibold text-gray-800">Upload Excel (.xlsx/.xls) or CSV (.csv)</h2>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                    onChange={handleExcelUpload}
                    className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition"
                  />
                  {excelData.length > 0 && (
                    <p className="mt-3 text-sm text-green-600 font-medium">
                      ✓ {excelData.length} unique rows loaded
                    </p>
                  )}
                </section>

                {/* Images */}
                <section className="bg-white rounded-2xl shadow-md p-6 border border-teal-100">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-7 h-7 rounded-full bg-teal-600 text-white text-sm font-bold flex items-center justify-center">2</span>
                    <h2 className="text-lg font-semibold text-gray-800">Upload JPG/PNG Images (optional)</h2>
                  </div>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,image/jpeg,image/png"
                    multiple
                    onChange={handleImagesUpload}
                    className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition"
                  />
                  {selectedImages.length > 0 && (
                    <div className="mt-3 space-y-1 text-sm">
                      <p className="text-green-600 font-medium">✓ {selectedImages.length} images selected</p>
                      <p className="text-teal-700">
                        Matched to PINs: <span className="font-semibold">{Object.keys(matchedImages).length}</span>
                      </p>
                      {unmatchedImages.length > 0 && (
                        <p className="text-amber-600">
                          ⚠ {unmatchedImages.length} image(s) don't match any PIN — they will be skipped.
                        </p>
                      )}
                    </div>
                  )}
                </section>
              </div>

              {/* Excel Preview */}
              {excelData.length > 0 && (
                <section className="bg-white rounded-2xl shadow-md p-6 border border-teal-100">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Excel Preview</h3>
                  <div className="overflow-auto max-h-64 rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-teal-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold text-teal-700">PIN</th>
                          <th className="px-4 py-2 text-left font-semibold text-teal-700">Circle</th>
                          <th className="px-4 py-2 text-left font-semibold text-teal-700">Location</th>
                          <th className="px-4 py-2 text-left font-semibold text-teal-700">Group</th>
                          <th className="px-4 py-2 text-left font-semibold text-teal-700">Image</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excelData.map((row, idx) => (
                          <tr key={`${row.pin}-${idx}`} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2 font-mono text-teal-800">{row.pin}</td>
                            <td className="px-4 py-2">{row.circle || "—"}</td>
                            <td className="px-4 py-2 max-w-[200px] truncate">{row.location || "—"}</td>
                            <td className="px-4 py-2">{row.group || "—"}</td>
                            <td className="px-4 py-2">
                              {matchedImages[row.pin] ? (
                                <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                                  <span>✓</span> Matched
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">No image</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Image preview grid */}
              {Object.keys(matchedImages).length > 0 && (
                <section className="bg-white rounded-2xl shadow-md p-6 border border-teal-100">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Matched Image Preview ({Object.keys(matchedImages).length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
                    {Object.entries(matchedImages).map(([pin, file]) => (
                      <div key={pin} className="border border-gray-200 rounded-xl p-2 bg-gray-50">
                        <div className="aspect-[3/4] bg-gray-200 rounded-lg overflow-hidden">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={pin}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <p className="mt-2 text-xs font-medium text-teal-700 truncate text-center">
                          {pin}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Upload button */}
              <div className="flex items-center gap-4">
                <button
                  onClick={uploadAll}
                  disabled={submitting || !excelData.length}
                  className="px-8 py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Uploading...
                    </span>
                  ) : (
                    "Upload My Properties"
                  )}
                </button>
                {excelData.length > 0 && !submitting && (
                  <button
                    onClick={() => { setExcelData([]); setSelectedImages([]); }}
                    className="px-5 py-3 rounded-xl bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Info notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                <strong>Note:</strong> These properties are stored separately and are only visible to you (the supervisor).
                Inspectors and other users cannot see or interact with them.
              </div>
            </div>
          )}

          {/* ── VIEW TAB ── */}
          {activeTab === "view" && (
            <div>
              {loadingUploads ? (
                <div className="text-center py-20 text-gray-500 animate-pulse">
                  Loading your uploaded properties...
                </div>
              ) : myUploads.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-gray-500 text-lg">No properties uploaded yet.</p>
                  <button
                    onClick={() => setActiveTab("upload")}
                    className="mt-4 px-5 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
                  >
                    Upload Now
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-4">
                    {myUploads.length} propert{myUploads.length === 1 ? "y" : "ies"} uploaded by you
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-5">
                    {myUploads.map((property) => (
                      <motion.div
                        key={property.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ scale: 1.03 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-xl shadow-md hover:shadow-xl cursor-pointer overflow-hidden border border-gray-100 relative"
                        onClick={() => openProperty(property)}
                      >
                        {/* Supervisor-only badge */}
                        <div className="absolute top-2 left-2 bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10">
                          MY UPLOAD
                        </div>
                        <div className="aspect-square overflow-hidden bg-gray-100">
                          {property.image ? (
                            <img
                              src={resolveImageUrl(property.image)}
                              alt={`Property ${property.pin}`}
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                              No Image
                            </div>
                          )}
                        </div>
                        <div className="p-3 text-center">
                          <p className="text-teal-800 font-semibold text-sm truncate">
                            PIN: {property.pin}
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5 truncate">
                            {property.location || "No location"}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* ── Property Detail Modal ── */}
      <AnimatePresence>
        {showPopup && selectedProperty && (
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 flex justify-between items-center p-4 border-b bg-gradient-to-r from-teal-100 to-teal-200 rounded-t-2xl">
                <div>
                  <h3 className="text-xl font-bold text-teal-800">
                    Property — {selectedProperty.pin}
                  </h3>
                  <span className="text-xs text-teal-600 font-medium">Supervisor Upload (private)</span>
                </div>
                <button
                  onClick={closePopup}
                  className="text-gray-500 hover:text-red-600 text-2xl font-bold w-8 h-8 flex items-center justify-center transition"
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {/* Image */}
                <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                  {selectedProperty.image ? (
                    <img
                      src={resolveImageUrl(selectedProperty.image)}
                      alt={`Property ${selectedProperty.pin}`}
                      className="w-full max-h-72 object-contain cursor-pointer hover:opacity-90 transition"
                      onClick={(e) => handleImageClick(e, resolveImageUrl(selectedProperty.image))}
                    />
                  ) : (
                    <div className="h-40 flex items-center justify-center text-gray-400">No Image</div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {[
                    ["PIN", selectedProperty.pin],
                    ["Circle", selectedProperty.circle],
                    ["Group", selectedProperty.group || selectedProperty.groupes],
                    ["Status", "Supervisor Upload"],
                  ].map(([label, val]) =>
                    val ? (
                      <span
                        key={label}
                        className="px-3 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200"
                      >
                        {label}: {val}
                      </span>
                    ) : null
                  )}
                </div>

                {/* Detail table */}
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 font-semibold text-gray-600 w-1/2">Field</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Location", selectedProperty.location],
                        ["Occupation Status", selectedProperty.occupationstatus],
                        ["Floor (Admin)", selectedProperty.floor_admin],
                        ["Land Use (Admin)", selectedProperty.landuse_admin],
                        ["Land Area (Admin)", selectedProperty.landarea_admin],
                        ["Covered Area (Admin)", selectedProperty.coveredarea_admin],
                        ["Exp. GARV", selectedProperty.exp_garv],
                        ["Admin GARV", selectedProperty.admin_garv],
                        ["Ref No", selectedProperty.refno],
                        ["P Name", selectedProperty.p_name],
                      ].map(([label, val]) => (
                        <tr key={label} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-600">{label}</td>
                          <td className="px-4 py-2 text-gray-800">{val || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 border-t bg-gray-50 p-4 rounded-b-2xl">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => handleDelete(selectedProperty.id)}
                    className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition"
                  >
                    Delete
                  </button>
                  <button
                    onClick={closePopup}
                    className="px-5 py-2 rounded-lg bg-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-300 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Fullscreen Image Modal ── */}
      <AnimatePresence>
        {showFullscreenImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFullscreen}
          >
            <div className="absolute top-5 right-5 flex gap-3 z-10">
              <button
                onClick={(e) => { e.stopPropagation(); setRotation((r) => (r + 90) % 360); }}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition"
                title="Rotate"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={closeFullscreen}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition"
              >
                <span className="text-white text-xl font-bold">×</span>
              </button>
            </div>
            <motion.img
              src={fullscreenImageUrl}
              alt="Fullscreen"
              animate={{ rotate: rotation }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupervisorBulkUpload;
